/**
 * Default configuration values for the Dual N-back test.
 *
 * @module nback/constants
 */

import type { TestConfig, VisualStimulus, AuditoryStimulus } from "./types";

/**
 * Default N-back level.
 * 1 means "compare with the immediately preceding trial."
 */
export const DEFAULT_N = 1;

/**
 * Total number of trials in a test run.
 * The first N trials are warm-up (no matches possible). Remaining are actual test trials.
 */
export const DEFAULT_TRIAL_COUNT = 6;

/** How long each stimulus is displayed (ms). */
export const DEFAULT_STIMULUS_DURATION = 750;

/** Pause between stimuli (ms). */
export const DEFAULT_INTER_STIMULUS_INTERVAL = 1250;

/** The visual shapes available for stimuli. */
export const VISUAL_STIMULI: readonly VisualStimulus[] = ["▲", "●", "■", "★"];

/**
 * Auditory stimulus frequencies (Hz).
 * These are standard musical notes: A4(440), C5(523), E5(659), G5(784).
 */
export const AUDITORY_STIMULI: readonly AuditoryStimulus[] = [
    440, 523, 659, 784,
];

/** Keyboard key for visual match response. */
export const VISUAL_KEY = "a";

/** Keyboard key for auditory match response. */
export const AUDITORY_KEY = "l";

/**
 * Creates a default test configuration.
 * The test runner can override specific fields.
 *
 * @param overrides - Optional fields to override defaults.
 * @returns A complete `TestConfig` object.
 */
export function createDefaultConfig(
    overrides?: Partial<TestConfig>
): TestConfig {
    return {
        n: DEFAULT_N,
        trialCount: DEFAULT_TRIAL_COUNT,
        stimulusDuration: DEFAULT_STIMULUS_DURATION,
        interStimulusInterval: DEFAULT_INTER_STIMULUS_INTERVAL,
        visualStimuli: VISUAL_STIMULI,
        auditoryStimuli: AUDITORY_STIMULI,
        visualKey: VISUAL_KEY,
        auditoryKey: AUDITORY_KEY,
        ...overrides,
    };
}
